// ============================================
// SEA SERVICE PAGE - i18n
// ============================================

import { createFileRoute } from '@tanstack/react-router';
import { useState, useRef, useMemo } from 'react';
import { Plus, Trash2, Edit2, Ship, Calendar, Loader2, Anchor, Upload, FileText } from 'lucide-react';
import { useMySeaService, useSeaServiceSummary, useCreateSeaService, useUpdateSeaService, useDeleteSeaService, useUploadSeaServiceDocument, useRanks } from '@/hooks';
import { formatDate, formatNumber, getCountries, getCountryName } from '@/utils';

// Gemi türleri - getter trap bypass
const VESSEL_TYPES: Record<string, { tr: string; en: string }> = {
  BULK_CARRIER: { tr: 'Dökme Yük Gemisi', en: 'Bulk Carrier' },
  TANKER: { tr: 'Tanker', en: 'Tanker' },
  CONTAINER: { tr: 'Konteyner Gemisi', en: 'Container Ship' },
  GENERAL_CARGO: { tr: 'Genel Kargo', en: 'General Cargo' },
  RORO: { tr: 'Ro-Ro', en: 'Ro-Ro' },
  PASSENGER: { tr: 'Yolcu Gemisi', en: 'Passenger Ship' },
  OFFSHORE: { tr: 'Açık Deniz', en: 'Offshore' },
  TUG: { tr: 'Römorkör', en: 'Tug' },
  YACHT: { tr: 'Yat', en: 'Yacht' },
  FISHING: { tr: 'Balıkçı Gemisi', en: 'Fishing Vessel' },
  OTHER: { tr: 'Diğer', en: 'Other' },
};

// Departmanlar - getter trap bypass
const DEPARTMENTS: Record<string, { tr: string; en: string }> = {
  DECK: { tr: 'Güverte', en: 'Deck' },
  ENGINE: { tr: 'Makine', en: 'Engine' },
  ELECTRICAL: { tr: 'Elektrik', en: 'Electrical' },
  CATERING: { tr: 'İkram', en: 'Catering' },
  SAFETY: { tr: 'Güvenlik', en: 'Safety' },
  MEDICAL: { tr: 'Sağlık', en: 'Medical' },
  RADIO: { tr: 'Telsiz', en: 'Radio' },
  CREWING: { tr: 'Crewing', en: 'Crewing' },
  FINANCE: { tr: 'Finans', en: 'Finance' },
  SALES: { tr: 'Satış', en: 'Sales' },
  IT: { tr: 'Bilgi Teknolojileri', en: 'IT' },
  LOGISTICS: { tr: 'Lojistik', en: 'Logistics' },
  MANAGEMENT: { tr: 'Yönetim', en: 'Management' },
  OFFSHORE: { tr: 'Açık Deniz', en: 'Offshore' },
  OTHER: { tr: 'Diğer', en: 'Other' },
};
import * as Dialog from '@radix-ui/react-dialog';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { SearchableSelect } from '@/components/SearchableSelect';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/profile/sea-service')({
  component: SeaServicePage,
});

function SeaServicePage() {
  const { t, i18n } = useTranslation(['panel', 'common', 'enums']);
  const countries = useMemo(() => getCountries(i18n.language), [i18n.language]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<any>(null);
  const [uploadingRecordId, setUploadingRecordId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Zod schema - t() ile çeviri desteği
  const seaServiceSchema = useMemo(() => z.object({
    vesselName: z.string().min(1, t('seaService.validation.vesselName')),
    vesselType: z.string().min(1, t('seaService.validation.vesselType')),
    imoNumber: z.string()
      .optional()
      .refine((val) => !val || /^\d{7}$/.test(val), {
        message: t('seaService.validation.imoNumber'),
      }),
    grossTonnage: z.coerce.number().optional(),
    flagState: z.string().min(2, t('seaService.validation.flagState')),
    department: z.string().min(1, t('seaService.validation.department')),
    engineBrand: z.string().optional(),
    enginePower: z.coerce.number().optional(),
    rankId: z.string().min(1, t('seaService.validation.rankId')),
    companyName: z.string().min(1, t('seaService.validation.companyName')),
    signOnDate: z.string().min(1, t('seaService.validation.signOnDate')),
    signOffDate: z.string().optional(),
    signOnPort: z.string().optional(),
    signOffPort: z.string().optional(),
  }), [t]);

  type SeaServiceFormData = z.infer<typeof seaServiceSchema>;

  const { data: recordsResponse, isLoading } = useMySeaService();
  const { data: summaryResponse } = useSeaServiceSummary();
  const { data: ranksResponse, isLoading: ranksLoading } = useRanks();
  const createMutation = useCreateSeaService();
  const updateMutation = useUpdateSeaService();
  const deleteMutation = useDeleteSeaService();
  const uploadMutation = useUploadSeaServiceDocument();

  const records: any[] = (recordsResponse as any)?.data || recordsResponse || [];
  const summary: any = (summaryResponse as any)?.data || summaryResponse;
  const ranks: any[] = (ranksResponse as any)?.data || ranksResponse || [];

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<SeaServiceFormData>({
    resolver: zodResolver(seaServiceSchema),
  });

  const onSubmit = (data: SeaServiceFormData) => {
    if (editingRecord) {
      updateMutation.mutate(
        { id: editingRecord.id, data },
        {
          onSuccess: () => {
            setIsModalOpen(false);
            setEditingRecord(null);
            reset();
          },
        }
      );
    } else {
      createMutation.mutate(data, {
        onSuccess: () => {
          setIsModalOpen(false);
          reset();
        },
      });
    }
  };

  const handleEdit = (record: any) => {
    setEditingRecord(record);
    reset({
      vesselName: record.vesselName || '',
      vesselType: record.vesselType || '',
      imoNumber: record.imoNumber || '',
      grossTonnage: record.grossTonnage || record.vesselGRT || undefined,
      flagState: record.flagState || record.vesselFlag || '',
      department: record.department || '',
      engineBrand: record.engineBrand || record.engineType || '',
      enginePower: record.enginePower || undefined,
      rankId: record.rankId || '',
      companyName: record.companyName || '',
      signOnDate: record.signOnDate ? record.signOnDate.split('T')[0] : '',
      signOffDate: record.signOffDate ? record.signOffDate.split('T')[0] : '',
      signOnPort: record.signOnPort || '',
      signOffPort: record.signOffPort || '',
    });
    setIsModalOpen(true);
  };

  const handleAddNew = () => {
    setEditingRecord(null);
    reset({
      vesselName: '',
      vesselType: '',
      imoNumber: '',
      grossTonnage: undefined,
      flagState: '',
      department: '',
      engineBrand: '',
      enginePower: undefined,
      rankId: '',
      companyName: '',
      signOnDate: '',
      signOffDate: '',
      signOnPort: '',
      signOffPort: '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = (id: string) => {
    if (confirm(t('seaService.confirm.delete'))) {
      deleteMutation.mutate(id);
    }
  };

  const handleFileUpload = (recordId: string) => {
    setUploadingRecordId(recordId);
    fileInputRef.current?.click();
  };

  const onFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && uploadingRecordId) {
      setUploadProgress(0);
      uploadMutation.mutate(
        { id: uploadingRecordId, file, onProgress: setUploadProgress },
        {
          onSuccess: () => {
            setUploadingRecordId(null);
            setUploadProgress(0);
          },
          onError: () => {
            setUploadingRecordId(null);
            setUploadProgress(0);
          },
        }
      );
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-maritime-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={onFileSelected}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('seaService.title')}</h1>
          <p className="text-gray-500 mt-1">{t('seaService.recordCount', { count: records.length })}</p>
        </div>
        <button onClick={handleAddNew} className="btn-primary">
          <Plus className="w-4 h-4" />
          {t('seaService.addRecord')}
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-maritime bg-maritime-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-maritime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('seaService.summary.totalDays')}</p>
                <p className="text-xl font-bold text-gray-900">{formatNumber(summary.totalDays || 0)}</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-maritime bg-ocean-100 flex items-center justify-center">
                <Ship className="w-5 h-5 text-ocean-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('seaService.summary.totalRecords')}</p>
                <p className="text-xl font-bold text-gray-900">{summary.totalRecords || 0}</p>
              </div>
            </div>
          </div>
          {summary.lastVessel && (
            <div className="card p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-maritime bg-green-100 flex items-center justify-center">
                  <Anchor className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('seaService.summary.lastVessel')}</p>
                  <p className="text-base font-medium text-gray-900 truncate">{summary.lastVessel.vesselName}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Records List */}
      {records.length === 0 ? (
        <div className="card p-12 text-center">
          <Ship className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('seaService.empty.title')}</h3>
          <p className="text-gray-500 mb-4">{t('seaService.empty.description')}</p>
          <button onClick={handleAddNew} className="btn-primary">
            <Plus className="w-4 h-4" />
            {t('seaService.empty.addFirst')}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {records.map((record: any) => {
            const isUploading = uploadingRecordId === record.id && uploadMutation.isPending;
            return (
              <div key={record.id} className="card p-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-maritime bg-maritime-100 flex items-center justify-center">
                    <Ship className="w-6 h-6 text-maritime-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-medium text-gray-900">{record.vesselName}</h3>
                        <p className="text-sm text-gray-500">{record.companyName}</p>
                      </div>
                      <span className="badge badge-info">
                        {(i18n.language === 'en' ? (record.rank?.nameEn || record.rank?.nameTr) : (record.rank?.nameTr || record.rank?.nameEn)) || record.rank?.name || t('seaService.position')}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                      <span>{VESSEL_TYPES[record.vesselType]?.[i18n.language === 'en' ? 'en' : 'tr'] || record.vesselType}</span>
                      <span>•</span>
                      <span>{getCountryName(record.flagState)}</span>
                      {record.grossTonnage && (
                        <>
                          <span>•</span>
                          <span>{formatNumber(record.grossTonnage)} GT</span>
                        </>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(record.signOnDate)} - {record.signOffDate ? formatDate(record.signOffDate) : t('seaService.ongoing')}</span>
                    </div>
                    {/* Document Status */}
                    <div className="mt-3 flex items-center gap-2">
                      {record.documentUrl ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                          <FileText className="w-3 h-3" />
                          {t('seaService.documentUploaded')}
                        </span>
                      ) : (
                        <button
                          onClick={() => handleFileUpload(record.id)}
                          disabled={isUploading}
                          className="inline-flex items-center gap-1 text-xs text-maritime-600 bg-maritime-50 px-2 py-1 rounded hover:bg-maritime-100 transition-colors"
                        >
                          {isUploading ? (
                            <>
                              <Loader2 className="w-3 h-3 animate-spin" />
                              {uploadProgress > 0 ? `%${uploadProgress}` : t('common:loading')}
                            </>
                          ) : (
                            <>
                              <Upload className="w-3 h-3" />
                              {t('seaService.uploadDocument')}
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => handleEdit(record)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-maritime"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(record.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-maritime"
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog.Root open={isModalOpen} onOpenChange={(open) => {
        setIsModalOpen(open);
        if (!open) setEditingRecord(null);
      }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto z-50">
            <Dialog.Title className="text-lg font-semibold text-gray-900 p-6 border-b border-gray-100">
              {editingRecord ? t('seaService.modal.editTitle') : t('seaService.modal.addTitle')}
            </Dialog.Title>
            <Dialog.Description className="sr-only">
              {editingRecord ? t('seaService.modal.editDescription') : t('seaService.modal.addDescription')}
            </Dialog.Description>
            
            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('seaService.form.vesselName')}</label>
                  <input type="text" className={`input ${errors.vesselName ? 'input-error' : ''}`} {...register('vesselName')} />
                </div>
                <div>
                  <label className="label">{t('seaService.form.imoNumber')}</label>
                  <input 
                    type="text" 
                    className={`input ${errors.imoNumber ? 'input-error' : ''}`}
                    maxLength={7}
                    placeholder="1234567"
                    onKeyPress={(e) => {
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    {...register('imoNumber')} 
                  />
                  {errors.imoNumber && (
                    <p className="text-red-500 text-xs mt-1">{errors.imoNumber.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('seaService.form.vesselType')}</label>
                  <select className={`input ${errors.vesselType ? 'input-error' : ''}`} {...register('vesselType')}>
                    <option value="">{t('common:select')}</option>
                    {Object.entries(VESSEL_TYPES).map(([key, labels]) => (
                      <option key={key} value={key}>{i18n.language === 'en' ? labels.en : labels.tr}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">{t('seaService.form.flagState')}</label>
                  <select className={`input ${errors.flagState ? 'input-error' : ''}`} {...register('flagState')}>
                    <option value="">{t('common:select')}</option>
                    {countries.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('seaService.form.grossTonnage')}</label>
                  <input type="number" className="input" {...register('grossTonnage')} />
                </div>
                <div>
                  <label className="label">{t('seaService.form.department')}</label>
                  <select className={`input ${errors.department ? 'input-error' : ''}`} {...register('department')}>
                    <option value="">{t('common:select')}</option>
                    {Object.entries(DEPARTMENTS).map(([key, labels]) => (
                      <option key={key} value={key}>{i18n.language === 'en' ? labels.en : labels.tr}</option>
                    ))}
                  </select>
                  {errors.department && <p className="text-red-500 text-sm mt-1">{errors.department.message}</p>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('seaService.form.position')}</label>
                  <Controller
                    name="rankId"
                    control={control}
                    render={({ field }) => (
                      <SearchableSelect
                        options={ranks.map((rank: any) => ({
                          value: rank.id,
                          label: i18n.language === 'en' ? (rank.nameEn || rank.nameTr) : (rank.nameTr || rank.nameEn),
                        }))}
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder={t('seaService.form.selectPosition')}
                        searchPlaceholder={t('seaService.form.searchPosition')}
                        isLoading={ranksLoading}
                      />
                    )}
                  />
                  {errors.rankId && <p className="text-red-500 text-sm mt-1">{errors.rankId.message}</p>}
                </div>
                <div>
                  <label className="label">{t('seaService.form.companyName')}</label>
                  <input type="text" className={`input ${errors.companyName ? 'input-error' : ''}`} {...register('companyName')} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('seaService.form.engineBrand')}</label>
                  <input type="text" className="input" placeholder={t('seaService.form.engineBrandPlaceholder')} {...register('engineBrand')} />
                </div>
                <div>
                  <label className="label">{t('seaService.form.enginePower')}</label>
                  <input type="number" className="input" placeholder={t('seaService.form.enginePowerPlaceholder')} {...register('enginePower')} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('seaService.form.signOnDate')}</label>
                  <input type="date" className={`input ${errors.signOnDate ? 'input-error' : ''}`} {...register('signOnDate')} />
                </div>
                <div>
                  <label className="label">{t('seaService.form.signOffDate')}</label>
                  <input type="date" className="input" {...register('signOffDate')} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('seaService.form.signOnPort')}</label>
                  <input type="text" className="input" {...register('signOnPort')} />
                </div>
                <div>
                  <label className="label">{t('seaService.form.signOffPort')}</label>
                  <input type="text" className="input" {...register('signOffPort')} />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <Dialog.Close asChild>
                  <button type="button" className="btn-secondary">{t('common:cancel')}</button>
                </Dialog.Close>
                <button type="submit" className="btn-primary" disabled={createMutation.isPending || updateMutation.isPending}>
                  {(createMutation.isPending || updateMutation.isPending) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {editingRecord ? t('common:update') : t('common:add')}
                </button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
